import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import moment from 'moment';
import Select from 'react-select';
import { useSelector } from 'react-redux';
import { DataTablePaginated } from '../components';
import { LeadStatusBadge } from '../components/Leads/LeadStatusBadge';
import { LeadDetailModal } from '../components/Leads/LeadDetailModal';
import { ACTIVITY_TYPE_LABELS } from '../components/Leads/leadActivityIcons';
import { axios } from '../libs/axios';
import ReactPaginate from 'react-paginate';
import { useTranslation } from '../i18n/I18nContext';
import { User } from '../states/actions';
import logger from '../utils/logger';
import { formatFromNow } from '../utils/relativeTime';

const SUPPORT_FILTER_OPTIONS = [
  { value: 'attention', labelKey: 'leads.filter.attention', countKey: 'attention' },
  { value: 'all', labelKey: 'leads.filter.all', countKey: 'all' },
  { value: 'contacted', labelKey: 'leads.filter.contacted', countKey: 'contacted' },
  { value: 'followup', labelKey: 'leads.filter.followup', countKey: 'followup' },
  { value: 'won', labelKey: 'leads.filter.won', countKey: 'won' },
  { value: 'lost', labelKey: 'leads.filter.lost', countKey: 'lost' },
];

const CONVERSION_FILTER_OPTIONS = [
  { value: 'quote_awaiting', labelKey: 'leads.filter.quoteAwaiting', countKey: 'quote_awaiting' },
  { value: 'unclaimed', labelKey: 'leads.filter.unclaimed', countKey: 'unclaimed' },
];

function formatConversionMoney(amount, locale) {
  if (amount == null || Number.isNaN(Number(amount))) return '';
  const loc = locale === 'et' ? 'et-EE' : 'en-EE';
  return new Intl.NumberFormat(loc, { style: 'currency', currency: 'EUR' }).format(Number(amount));
}

function formatLeadClientMeta(row, t) {
  const parts = [row.clientEmail, row.clientPhone].filter(Boolean);
  if (row.locale && String(row.locale).trim()) {
    parts.push(String(row.locale).trim().toUpperCase());
  }
  return parts.length ? parts.join(' · ') : t('common.emptyValue');
}

function conversionTypeLabel(type, t) {
  const key = String(type || '').toLowerCase();
  if (!key) return '';
  const map = {
    general: 'leads.conversionType.general',
    personal: 'leads.conversionType.personal',
    'hugo+': 'leads.conversionType.hugoPlus',
    quote: 'leads.conversionType.quote',
  };
  return t(map[key] || 'leads.conversionType.unknown');
}

function formatDateDMY(date) {
  if (!date) return '';
  return moment(date).format('DD.MM.YYYY');
}

function isOverdue(nextActionAt, isOpen) {
  if (!nextActionAt || !isOpen) return false;
  return moment(nextActionAt).isSameOrBefore(moment(), 'day');
}

export const Leads = () => {
  const { t, locale } = useTranslation();
  const user = useSelector(User.get());
  const isAdmin = user?.role === 'admin';
  const attentionFallbackRef = useRef(true);
  const [lists, setLists] = useState([]);
  const [selectedListId, setSelectedListId] = useState('');
  const [entries, setEntries] = useState([]);
  const [filterCounts, setFilterCounts] = useState({});
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [sizePerPage] = useState(100);
  const [activeFilter, setActiveFilter] = useState('attention');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [showNewListModal, setShowNewListModal] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [savingList, setSavingList] = useState(false);
  const [renameListName, setRenameListName] = useState('');
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [refreshingConversion, setRefreshingConversion] = useState(false);
  const [modalEntryId, setModalEntryId] = useState(null);

  const fetchLists = useCallback(async () => {
    try {
      const response = await axios.get('/leads/lists');
      const nextLists = Array.isArray(response?.lists) ? response.lists : [];
      setLists(nextLists);
      setSelectedListId((prev) => {
        if (prev && nextLists.some((l) => String(l._id) === String(prev))) return prev;
        return nextLists[0]?._id ? String(nextLists[0]._id) : '';
      });
    } catch (error) {
      logger.error(error);
      setLists([]);
    }
  }, []);

  const fetchEntries = useCallback(async (listId, page = 1, filter = activeFilter, searchTerm = search) => {
    if (!listId) {
      setEntries([]);
      setTotalRecords(0);
      setFilterCounts({});
      return;
    }
    setLoading(true);
    try {
      const response = await axios.get(`/leads/lists/${listId}/entries`, {
        params: {
          page,
          sizePerPage,
          filter,
          search: searchTerm || undefined,
        },
      });

      if (
        filter === 'attention' &&
        attentionFallbackRef.current &&
        !searchTerm &&
        (response?.total || 0) === 0 &&
        (response?.filterCounts?.all || 0) > 0
      ) {
        attentionFallbackRef.current = false;
        setActiveFilter('all');
        return;
      }
      attentionFallbackRef.current = false;

      setEntries(Array.isArray(response?.entries) ? response.entries : []);
      setTotalRecords(response?.total || 0);
      setFilterCounts(response?.filterCounts || {});
    } catch (error) {
      logger.error(error);
      setEntries([]);
      setTotalRecords(0);
      setFilterCounts({});
    } finally {
      setLoading(false);
    }
  }, [sizePerPage, activeFilter, search]);

  useEffect(() => {
    attentionFallbackRef.current = true;
  }, [selectedListId]);

  useEffect(() => {
    fetchLists();
  }, [fetchLists]);

  useEffect(() => {
    if (selectedListId) {
      fetchEntries(selectedListId, currentPage);
    }
  }, [selectedListId, currentPage, activeFilter, search, fetchEntries]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const selectedList = useMemo(
    () => lists.find((l) => String(l._id) === String(selectedListId)),
    [lists, selectedListId],
  );

  const listOptions = useMemo(
    () => lists.map((list) => ({
      value: list._id,
      label: `${list.name} (${list.entryCount || 0})`,
    })),
    [lists],
  );

  const handleCreateList = async (e) => {
    e.preventDefault();
    const name = newListName.trim();
    if (!name) return;
    try {
      setSavingList(true);
      const response = await axios.post('/leads/lists', { name });
      const list = response?.list;
      await fetchLists();
      if (list?._id) {
        setSelectedListId(String(list._id));
      }
      setShowNewListModal(false);
      setNewListName('');
      window.nSuccess({ text: t('leads.listCreated') });
    } catch (error) {
      window.nError({ text: error?.message || t('leads.listCreateError') });
    } finally {
      setSavingList(false);
    }
  };

  const handleRenameList = async (e) => {
    e.preventDefault();
    const name = renameListName.trim();
    if (!name || !selectedListId) return;
    try {
      setSavingList(true);
      await axios.put(`/leads/lists/${selectedListId}`, { name });
      await fetchLists();
      setShowRenameModal(false);
      window.nSuccess({ text: t('leads.listRenamed') });
    } catch (error) {
      window.nError({ text: error?.message || t('leads.listRenameError') });
    } finally {
      setSavingList(false);
    }
  };

  const handleDeleteList = () => {
    if (!selectedListId || !selectedList) return;
    window.nConfirm({
      title: t('leads.deleteListTitle'),
      text: t('leads.deleteListConfirm', { name: selectedList.name }),
      icon: 'fa-exclamation-circle',
      onYes: async () => {
        try {
          await axios.delete(`/leads/lists/${selectedListId}`);
          setSelectedListId('');
          setCurrentPage(1);
          await fetchLists();
          window.nSuccess({ text: t('leads.listDeleted') });
        } catch (error) {
          window.nError({ text: error?.message || t('leads.listDeleteError') });
        }
      },
    });
  };

  const handleRemoveEntry = useCallback((entry) => {
    window.nConfirm({
      title: t('leads.removeEntryTitle'),
      text: t('leads.removeEntryConfirm', { name: entry.clientName || entry.clientEmail }),
      icon: 'fa-exclamation-circle',
      onYes: async () => {
        try {
          await axios.delete(`/leads/lists/${selectedListId}/entries/${entry._id}`);
          await fetchEntries(selectedListId, currentPage);
          await fetchLists();
          window.nSuccess({ text: t('leads.entryRemoved') });
        } catch (error) {
          window.nError({ text: error?.message || t('leads.entryRemoveError') });
        }
      },
    });
  }, [t, selectedListId, currentPage, fetchEntries, fetchLists]);

  const handleRefreshConversions = async () => {
    if (!selectedListId) return;
    try {
      setRefreshingConversion(true);
      const response = await axios.post(`/leads/lists/${selectedListId}/refresh-conversions`);
      await fetchEntries(selectedListId, currentPage);
      window.nSuccess({
        text: t('leads.refreshConversionsDone', {
          total: response?.total ?? 0,
          won: response?.wonCount ?? 0,
          paid: response?.paidCount ?? 0,
          awaiting: response?.awaitingCount ?? 0,
        }),
      });
    } catch (error) {
      window.nError({ text: error?.message || t('leads.refreshConversionsError') });
    } finally {
      setRefreshingConversion(false);
    }
  };

  const filterOptions = useMemo(
    () => [...SUPPORT_FILTER_OPTIONS, ...CONVERSION_FILTER_OPTIONS],
    [],
  );

  const handleStatusChange = useCallback(async (entryId, payload) => {
    try {
      const response = await axios.patch(`/leads/entries/${entryId}/status`, payload);
      const updated = response?.entry;
      if (updated) {
        setEntries((prev) => prev.map((e) => (String(e._id) === String(entryId) ? { ...e, ...updated } : e)));
        if (updated.nextActionAt && payload.status !== 'lost') {
          window.nSuccess({
            text: t('leads.followUpSet', { date: formatDateDMY(updated.nextActionAt) }),
          });
        }
        await fetchEntries(selectedListId, currentPage);
      }
      return updated;
    } catch (error) {
      window.nError({ text: error?.message || t('leads.statusUpdateError') });
      throw error;
    }
  }, [t, selectedListId, currentPage, fetchEntries]);

  const handleEntryUpdated = useCallback((updated) => {
    if (!updated?._id) return;
    setEntries((prev) => prev.map((e) => (String(e._id) === String(updated._id) ? { ...e, ...updated } : e)));
    fetchEntries(selectedListId, currentPage);
  }, [selectedListId, currentPage, fetchEntries]);

  const pageCount = Math.max(1, Math.ceil(totalRecords / sizePerPage));

  const tableColumns = useMemo(() => {
    const col = (width) => ({
      style: { width, minWidth: 0 },
      headerStyle: { width, minWidth: 0 },
    });

    return [
      {
        dataField: 'clientName',
        text: t('leads.colClient'),
        ...col('28%'),
        formatter: (_value, row) => (
          <div className="lead-client-cell">
            <div className="lead-client-name">{row.clientName || t('common.emptyValue')}</div>
            <div className="lead-client-meta">
              {formatLeadClientMeta(row, t)}
            </div>
          </div>
        ),
      },
      {
        dataField: 'displayStatus',
        text: t('leads.colStatus'),
        sort: false,
        classes: 'cell-lead-status',
        headerClasses: 'cell-lead-status',
        ...col('14%'),
        formatter: (_value, row) => (
          <LeadStatusBadge
            entry={row}
            t={t}
            onStatusChange={handleStatusChange}
          />
        ),
      },
      {
        dataField: 'lastActivityAt',
        text: t('leads.colLastActivity'),
        sort: false,
        ...col('22%'),
        formatter: (_value, row) => {
          if (!row.lastActivityAt) return t('common.emptyValue');
          const typeKey = ACTIVITY_TYPE_LABELS[row.lastActivityType] || ACTIVITY_TYPE_LABELS.note;
          return (
            <span>
              {formatFromNow(row.lastActivityAt, locale)} · {t(typeKey)}
            </span>
          );
        },
      },
      {
        dataField: 'nextActionAt',
        text: t('leads.colNextAction'),
        sort: false,
        ...col('18%'),
        formatter: (_value, row) => {
          if (!row.nextActionAt) return t('common.emptyValue');
          const overdue = isOverdue(row.nextActionAt, row.isOpen);
          return (
            <span className={overdue ? 'lead-next-overdue' : ''}>
              {formatDateDMY(row.nextActionAt)}
              {overdue && <span className="lead-overdue-hint"> · {t('leads.overdue')}</span>}
            </span>
          );
        },
      },
      {
        dataField: 'conversion',
        text: t('leads.colConversion'),
        sort: false,
        ...col(isAdmin ? '18%' : '20%'),
        formatter: (_value, row) => {
          const typeLabel = conversionTypeLabel(row.conversionType, t);
          const state = row.conversionState || '';
          const comparison = row.conversionComparison || '';
          let valueLine = '';
          if (state === 'paid' && row.conversionAmount != null) {
            valueLine = formatConversionMoney(row.conversionAmount, locale);
          } else if (state === 'awaiting' && row.conversionType === 'quote') {
            valueLine = t('leads.conversionAwaitingInvoice');
          } else if (!typeLabel && !state) {
            return t('common.emptyValue');
          }

          const projectNo = row.conversionProjectNo;
          return (
            <div className="lead-conversion-cell">
              <div>
                {typeLabel}
                {valueLine ? ` · ${valueLine}` : ''}
              </div>
              {projectNo ? (
                <Link
                  to={`/projects/${projectNo}`}
                  className="btn-link-page"
                  onClick={(e) => e.stopPropagation()}
                >
                  {projectNo}
                </Link>
              ) : null}
              {comparison === 'unclaimed' ? (
                <div className="lead-conversion-comparison">
                  {t('leads.conversionComparison.unclaimed')}
                </div>
              ) : null}
            </div>
          );
        },
      },
      ...(isAdmin ? [{
        dataField: 'actions',
        text: '',
        sort: false,
        classes: 'cell-actions',
        headerClasses: 'cell-actions',
        ...col('8%'),
        formatter: (_cell, row) => (
          <div className="actions">
            <button
              type="button"
              className="button button-small"
              onClick={(e) => {
                e.stopPropagation();
                handleRemoveEntry(row);
              }}
            >
              {t('common.delete')}
            </button>
          </div>
        ),
      }] : []),
    ];
  }, [t, locale, isAdmin, handleRemoveEntry, handleStatusChange]);

  return (
    <div className="row table_v2 leads">
      <div className="col p-0">
        <div className="x_panel chatbot-builder">
          <div className="header-menu d-flex justify-content-between align-items-center">
            <div className="header-title d-flex align-items-center">
              <h1>{t('leads.title')}</h1>
            </div>
            <div className="header-actions d-flex align-items-center">
              {selectedListId && (
                <button
                  type="button"
                  className="button button-blue"
                  style={{ marginRight: '8px' }}
                  onClick={handleRefreshConversions}
                  disabled={refreshingConversion || loading}
                >
                  {refreshingConversion ? t('leads.refreshConversionsRunning') : t('leads.refreshConversions')}
                </button>
              )}
              {isAdmin && (
              <button
                type="button"
                className="button button-blue"
                onClick={() => {
                  setNewListName('');
                  setShowNewListModal(true);
                }}
              >
                {t('leads.newList')}
              </button>
              )}
            </div>
          </div>
          <div className="x_content">
            <div className="page-container d-flex flex-column">
              <div className="page-header d-flex align-items-center flex-wrap">
                <div className="filter filter-pagesize leads-list-select d-flex align-items-center">
                  <Select
                    className="leads-list-select__wrapper"
                    classNamePrefix="leads-list-select"
                    options={listOptions}
                    value={listOptions.find((o) => String(o.value) === String(selectedListId)) || null}
                    onChange={(opt) => {
                      setSelectedListId(opt?.value ? String(opt.value) : '');
                      setCurrentPage(1);
                    }}
                    isClearable={false}
                    isSearchable={lists.length > 8}
                    placeholder={t('leads.noLists')}
                    noOptionsMessage={() => t('leads.noLists')}
                  />
                </div>
                {isAdmin && selectedList && (
                  <div className="leads-list-actions d-flex align-items-center">
                    <button
                      type="button"
                      className="button button-small"
                      onClick={() => {
                        setRenameListName(selectedList.name);
                        setShowRenameModal(true);
                      }}
                    >
                      {t('common.edit')}
                    </button>
                    <button
                      type="button"
                      className="button button-small"
                      onClick={handleDeleteList}
                      style={{ marginLeft: '8px' }}
                    >
                      {t('common.delete')}
                    </button>
                  </div>
                )}
              </div>

              {selectedListId && (
                <div className="leads-filters d-flex flex-wrap align-items-center">
                  {filterOptions.map((opt) => {
                    const count = filterCounts[opt.countKey];
                    const label = count != null
                      ? `${t(opt.labelKey)} (${count})`
                      : t(opt.labelKey);
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        className={`status status-${opt.value}${activeFilter === opt.value ? ' selected' : ''}`}
                        onClick={() => {
                          attentionFallbackRef.current = false;
                          setActiveFilter(opt.value);
                          setCurrentPage(1);
                        }}
                      >
                        {label}
                      </button>
                    );
                  })}
                  <input
                    type="search"
                    className="leads-search"
                    placeholder={t('leads.searchPlaceholder')}
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                  />
                </div>
              )}

              <div className="page-content">
                {!selectedListId ? (
                  <p>{t('leads.emptyNoList')}</p>
                ) : (
                  <DataTablePaginated
                    data={entries}
                    columns={tableColumns}
                    keyField="_id"
                    noDataIndication={loading ? t('common.loading') : t('leads.emptyList')}
                    totalRecords={totalRecords}
                    rowClasses={(row) => (row.status === 'won' ? 'row-lead-converted' : '')}
                    onRowClick={(row) => setModalEntryId(row._id)}
                  />
                )}
              </div>

              {selectedListId && pageCount > 1 && (
                <div className="page-footer d-flex justify-content-between align-items-center">
                  <ReactPaginate
                    containerClassName="pagination"
                    activeClassName="active"
                    previousLinkClassName="fa-regular fa-chevron-left"
                    nextLinkClassName="fa-regular fa-chevron-right"
                    previousClassName={currentPage === 1 ? 'd-none' : 'previous'}
                    nextClassName={currentPage === pageCount ? 'd-none' : 'next'}
                    previousLabel={t('common.previous')}
                    nextLabel={t('common.next')}
                    breakLabel={t('common.ellipsis')}
                    marginPagesDisplayed={2}
                    pageRangeDisplayed={5}
                    pageCount={pageCount}
                    onPageChange={(e) => setCurrentPage(e.selected + 1)}
                    forcePage={currentPage - 1}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <LeadDetailModal
        entryId={modalEntryId}
        listId={selectedListId}
        visible={!!modalEntryId}
        onClose={() => setModalEntryId(null)}
        onUpdated={handleEntryUpdated}
        t={t}
        locale={locale}
        LeadStatusBadge={LeadStatusBadge}
      />

      {showNewListModal && (
        <div className="modal prepayment" onClick={() => setShowNewListModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modalclose" onClick={() => setShowNewListModal(false)}>&times;</span>
              <h2>{t('leads.newListTitle')}</h2>
            </div>
            <div className="modal-form d-flex flex-row flex-wrap">
              <form onSubmit={handleCreateList} style={{ width: '100%', display: 'contents' }}>
                <div className="form-row col-thin">
                  <label style={{ marginBottom: '8px', fontWeight: '500' }}>{t('leads.listName')}</label>
                  <input
                    type="text"
                    value={newListName}
                    onChange={(e) => setNewListName(e.target.value)}
                    style={{ width: '100%' }}
                    autoFocus
                    required
                  />
                </div>
                <div className="form-row col-full row-actions">
                  <button type="button" className="button button-large" onClick={() => setShowNewListModal(false)}>
                    {t('common.cancel')}
                  </button>
                  <button type="submit" className="button button-large button-blue" disabled={savingList}>
                    {savingList ? t('common.saving') : t('common.create')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {showRenameModal && (
        <div className="modal prepayment" onClick={() => setShowRenameModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modalclose" onClick={() => setShowRenameModal(false)}>&times;</span>
              <h2>{t('leads.renameListTitle')}</h2>
            </div>
            <div className="modal-form d-flex flex-row flex-wrap">
              <form onSubmit={handleRenameList} style={{ width: '100%', display: 'contents' }}>
                <div className="form-row col-thin">
                  <label style={{ marginBottom: '8px', fontWeight: '500' }}>{t('leads.listName')}</label>
                  <input
                    type="text"
                    value={renameListName}
                    onChange={(e) => setRenameListName(e.target.value)}
                    style={{ width: '100%' }}
                    autoFocus
                    required
                  />
                </div>
                <div className="form-row col-full row-actions">
                  <button type="button" className="button button-large" onClick={() => setShowRenameModal(false)}>
                    {t('common.cancel')}
                  </button>
                  <button type="submit" className="button button-large button-blue" disabled={savingList}>
                    {savingList ? t('common.saving') : t('common.update')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
